import { Paper, TextField, Box, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { ToastSeverity, confirmDialogState, toastStateSelector } from 'state';
import { query } from 'operations';
import { DialogTypes, GeneralIds, ModelTypes } from 'types/types';
import { Patient } from 'api/models';
import { getDateString } from 'utils';
import { remove } from 'operations';
import PatientsTable from './PatientsTable';

interface PatientFilters {
  name?: string;
  birthDate?: string;
  identifier?: string;
  projectId?: string;
  gender?: string;
}

export default function Patients() {
  const { t, i18n } = useTranslation();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [queriedPatients, setQueriedPatients] = useState<Patient[]>([]);
  const [filters, setFilters] = useState<PatientFilters>({});
  const setDialogStore = useSetRecoilState(confirmDialogState);
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectId } = useParams<GeneralIds>();
  const setToast = useSetRecoilState(toastStateSelector);
  const [loading, setLoading] = useState(true);

  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const { data, meta } = await query<Patient[]>({
          type: ModelTypes.patient,
          ids: { projectId },
          queryParams: {
            pageSize: pageSize,
            pageNumber: pageNumber,
            query: searchParams.get('searchText'),
          },
        });
        setTotalCount(meta.totalCount ?? 0);
        setPatients(data);
        setQueriedPatients(data);
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.fetch'),
          severity: ToastSeverity.error,
        });
        setPatients([]);
      }
      setLoading(false);
    }

    fetchData();
  }, [pageSize, pageNumber, projectId, searchParams, t, i18n, setToast]);

  const onPatientDeleteConfirm = (patientId: string) => {
    return async () => {
      try {
        await remove({
          type: ModelTypes.patient,
          ids: {
            id: patientId,
            projectId: projectId,
          },
        });
        const { data: patients } = await query<Patient[]>({
          type: ModelTypes.patient,
          ids: { projectId },
          queryParams: {
            pageSize,
            pageNumber,
          },
        });

        setPatients(patients);
        setToast({
          isOpen: true,
          message: t('general.success.delete'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.delete'),
          severity: ToastSeverity.error,
        });
      }
    };
  };

  const onDelete = (patientId: string) => {
    setDialogStore({
      isOpen: true,
      dialogType: DialogTypes.confirmDelete,
      action: onPatientDeleteConfirm(patientId),
    });
  };

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value.toLowerCase() };
    setFilters(newFilters);
    if (value === '') {
      return setPatients(queriedPatients);
    }
    const filteredPatients = queriedPatients.filter(patient => {
      let name = true;
      let birthDate = true;
      let identifier = true;
      let projectId = true;
      let gender = true;
      if (newFilters.name) {
        name =
          (`${patient.firstName?.toLowerCase()} ${patient.lastName?.toLowerCase()}`.includes(
            newFilters.name,
          ) ||
            `${patient.lastName?.toLowerCase()} ${patient.firstName?.toLowerCase()}`.includes(
              newFilters.name,
            )) ??
          false;
      }

      if (newFilters.birthDate) {
        birthDate = getDateString(patient.birthDate ?? '')
          ?.toLowerCase()
          .startsWith(newFilters.birthDate);
      }

      if (newFilters.identifier) {
        identifier =
          patient.identifiers?.some(identifier =>
            identifier.id.toLowerCase().includes(newFilters.identifier ?? ''),
          ) ?? false;
      }

      if (newFilters.projectId) {
        projectId =
          patient.patientProjectId
            ?.toLowerCase()
            .includes(newFilters.projectId) ?? false;
      }

      if (newFilters.gender) {
        gender = patient.gender?.toLowerCase() === newFilters.gender;
      }

      return name && birthDate && identifier && projectId && gender;
    });

    setPatients(filteredPatients);
  };

  return (
    <Paper sx={{ width: '100%', mt: 3, p: 2 }}>
      <Box display="flex" alignItems="center">
        <TextField
          label={t('general.search')}
          variant="outlined"
          fullWidth
          value={searchParams.get('searchText')}
          onChange={e => setSearchParams({ searchText: e.target.value })}
        />
        <IconButton sx={{ position: 'absolute', right: 40 }} size="small">
          <SearchIcon fontSize="small" />
        </IconButton>
      </Box>
      <PatientsTable
        patients={patients}
        onDelete={onDelete}
        onFilterChange={onFilterChange}
        filters={filters}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        totalCount={totalCount}
        loading={loading}
      />
    </Paper>
  );
}
